import React from 'react';
import { Coins, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * CreditCost - A component to display the cost of an action in credits
 * 
 * Usage:
 * <CreditCost cost={10} action="Generate vocabulary text" />
 * <CreditCostButton cost={5} onClick={handleGenerate}>Generate</CreditCostButton>
 */

// Activity costs in credits
export const CREDIT_COSTS = {
  VOCABULARY_TEXT: 5,
  TOPIC_GENERATION: 10,
  BATCH_GENERATION_PER_LANG: 10,
  AI_QUESTION: 2,
  TTS_PREMIUM: 1,
  TRANSLATION: 1,
};

// Credit cost display component
export function CreditCost({ cost, action, className = '', size = 'sm' }) {
  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
  };

  return (
    <div className={`flex items-center gap-1.5 text-amber-500/80 ${sizeClasses[size]} ${className}`}>
      <Coins className={`${size === 'xs' ? 'w-3 h-3' : size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'}`} />
      <span>{cost} credits</span>
      {action && <span className="text-slate-500">• {action}</span>}
    </div>
  );
}

// Credit cost badge - for inline display
export function CreditBadge({ cost, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-xs font-medium ${className}`}>
      <Coins className="w-3 h-3" />
      {cost}
    </span>
  );
}

// Button with credit cost display
export function CreditCostButton({ 
  cost, 
  children, 
  onClick, 
  disabled, 
  userCredits = Infinity,
  className = '',
  variant = 'default',
  ...props 
}) {
  const hasEnoughCredits = userCredits >= cost;
  
  const handleClick = (e) => {
    if (!hasEnoughCredits) {
      e.preventDefault();
      toast.error(`Insufficient credits. You need ${cost} credits but only have ${userCredits}.`);
      return;
    }
    onClick?.(e);
  };

  const baseClasses = "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition";
  const variantClasses = {
    default: "bg-amber-500 hover:bg-amber-600 text-black",
    outline: "border border-amber-500/30 bg-transparent hover:bg-amber-500/10 text-amber-400",
    ghost: "bg-transparent hover:bg-amber-500/10 text-amber-400",
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled || !hasEnoughCredits}
      className={`${baseClasses} ${variantClasses[variant]} disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      {...props}
    >
      {children}
      <span className="flex items-center gap-1 text-sm opacity-80">
        <Coins className="w-3.5 h-3.5" />
        {cost}
      </span>
    </button>
  );
}

// Warning for low credits
export function LowCreditWarning({ credits, threshold = 50 }) {
  if (credits >= threshold) return null;

  return (
    <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
      <AlertCircle className="w-5 h-5 flex-shrink-0" />
      <div>
        <p className="font-medium">Low on credits</p>
        <p className="text-amber-400/70">You have {credits} credits remaining. Some features may be limited.</p>
      </div>
    </div>
  );
}

// Confirmation dialog for credit costs
export function confirmCreditCost(cost, action, userCredits) {
  if (userCredits < cost) {
    toast.error(`Insufficient credits. You need ${cost} credits but only have ${userCredits}.`);
    return false;
  }
  
  // For small costs, auto-approve
  if (cost <= 5) {
    return true;
  }
  
  // For larger costs, show a toast confirmation
  return window.confirm(`This action will cost ${cost} credits. You have ${userCredits} credits. Continue?`);
}

export default CreditCost;
